"use client";
import React, { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";

const INCLUDES = [
  {
    img: "/next_assets/images/financial-planning/goal-based-img.svg",
    title: "Goal-based SIP Suggestions",
    desc: "Personalized to your age & income",
  },
  {
    img: "/next_assets/images/financial-planning/inflation-img.svg",
    title: "Inflation Adjustments",
    desc: "Ensures future-ready planning",
  },
  {
    img: "/next_assets/images/financial-planning/risk-adjust-img.svg",
    title: "Risk-Adjusted Portfolio",
    desc: "Balances growth & safety over time",
  },
  {
    img: "/next_assets/images/financial-planning/portfolio.svg",
    title: "Portfolio Tracking",
    desc: "Clarity on how to reach your goals faster",
  },
  {
    img: "/next_assets/images/financial-planning/personalized.svg",
    title: "Personalized Advisory Support",
    desc: "Expert guidance tailored to your life goals",
  },
];

export default function PlanIncludes() {
  const pathname = usePathname();
  const [activeIndex, setActiveIndex] = useState(0);

  const isRetirement = pathname.includes(
    "/financial-planning/retirement-planning",
  );
  const { borderColor, gradientBg } = useMemo(() => {
    return {
      borderColor: isRetirement ? "#003D6C" : "#1A51AA",
      gradientBg: isRetirement
        ? "radial-gradient(66.97% 50% at 50% 50%, rgba(0,61,108,0.35) 0%, #003D6C 100%)"
        : "radial-gradient(66.97% 50% at 50% 50%, rgba(26,81,170,0.35) 0%, #1A51AA 100%)",
    };
  }, [pathname]);

  useEffect(() => {
    const timer = setTimeout(
      () => setActiveIndex((prev) => (prev + 1) % INCLUDES.length),
      4000,
    );
    return () => clearTimeout(timer);
  }, [activeIndex]);

  return (
    <section className="mb-17.5 xxs:mb-100 2md:mb-20 2xl:mb-100 text-center">
      <div className="flex flex-col xxs:flex-row justify-center items-start px-0 shadow-[0px_4px_30.6px_0px_#0000000F] rounded-8xl bg-white">
        <div
          style={{ background: gradientBg }}
          className="relative xxs:w-90 2md:w-127.5 2lg:w-152.5 w-146 xxs:h-500 2md:h-600 rounded-2xl xxs:rounded-none xxs:rounded-tl-8xl xxs:rounded-bl-8xl overflow-hidden  xxs:flex items-center justify-center xxs:px-10"
        >
          <h2 className="text-2xl leading-tight font-semibold text-white mb-2.5 text-left block xxs:hidden p-6 pb-0">
            {isRetirement
              ? " What Your Retirement Plan Includes "
              : " What Your Financial Plan Includes "}
          </h2>
          <p className="text-left text-sm font-normal text-white mb-4  block xxs:hidden px-6 xxs:px-0">
            {isRetirement
              ? " Get all you need for retirement planning, smart savings, investment strategies and income planning."
              : " Everything you need for smart financial planning that adapts with your life goals."}
          </p>

          <div className="relative w-full block xxs:hidden">
            <div className="p-4 w-full" style={{ background: borderColor }}>
              <p className="text-center text-base font-normal text-white px-0 transition-all duration-500 ease-in-out">
                {INCLUDES[activeIndex].desc}
              </p>
            </div>

            <div className="absolute bottom-0 left-0 h-1 w-full bg-white/30 overflow-hidden">
              <div
                key={activeIndex}
                className="h-1 bg-white animate-progressBar"
              />
            </div>
          </div>

          {INCLUDES.map((item, i) => (
            <Image
              key={i}
              src={item.img}
              alt={item.title}
              width={360}
              height={481}
              className={`w-65 xxs:w-300 2md:w-90 h-85 xxs:h-99 2md:h-120.25 absolute -bottom-1 left-1/2 -translate-x-1/2 transition-opacity duration-700 ease-in-out ${
                i === activeIndex ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
        </div>

        <div className="flex-1 xxs:p-5 2md:p-10 rounded-tr-8xl rounded-br-8xl bg-white hidden xxs:block h-full">
          <h2 className="text-[24px] 2md:text-2.5xl 2xl:text-[32px] leading-tight font-semibold text-primaryBlack mb-2.5 text-left">
            {isRetirement
              ? " What Your Retirement Plan Includes "
              : " What Your Financial Plan Includes "}
          </h2>
          <p className="2md:text-lg 2xl:text-lg font-normal text-primaryBlack mb-8 text-left">
            {isRetirement
              ? " Get all you need for retirement planning, smart savings, investment strategies and income planning."
              : " Everything you need for smart financial planning that adapts with your life goals."}
          </p>

          <div
            className="grid gap-5 border-2 p-3 rounded-4xl bg-[#F5F5F5]"
            style={{ borderColor }}
          >
            {INCLUDES.map((item, i) => {
              const isActive = i === activeIndex;
              const isDone = i < activeIndex;

              return (
                <div
                  key={i}
                  className={`px-4 mb-2 rounded-2xl flex flex-col items-start transition-all duration-300 ease-in-out relative ${isActive ? "p-4 mb-0!" : ""}`}
                  style={{
                    backgroundColor: isActive ? borderColor : "transparent",
                  }}
                >
                  <p
                    className={`flex items-center gap-2 2md:text-xl ${
                      isActive
                        ? "text-white font-semibold"
                        : isDone
                          ? "text-primaryBlue font-semibold"
                          : "text-[#20202080] font-medium"
                    }`}
                  >
                    {isDone && (
                      <span className="text-green-500 font-bold text-2xl">
                        ✓
                      </span>
                    )}
                    {item.title}
                  </p>

                  {isActive && (
                    <>
                      <span className="text-sm font-medium text-[#FFFFFFCC] text-left">
                        {item.desc}
                      </span>
                      <div className="absolute bottom-0 left-0 h-1 w-full bg-white/30 rounded-b-2xl overflow-hidden">
                        <div className="h-1 bg-white rounded-b-2xl animate-progressBar" />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes progressBar {
          0% {
            transform: scaleX(0);
          }
          100% {
            transform: scaleX(1);
          }
        }
        .animate-progressBar {
          animation: progressBar 4s linear forwards;
          transform-origin: left;
        }
      `}</style>
    </section>
  );
}
